import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { isStaffRole } from "@lms/backend/lib/auth/post-login-redirect";
import { useAuthStore } from "@/stores/authStore";
import { fetchStaffStudentDetail } from "@/services/api/instructor";
import { ApiDownloadButton } from "@/components/api-download-button";
import { StaffStudentQuizAttempts } from "@/components/staff-student-quiz-attempts";

function formatWhen(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function formatPrice(currency: string, amount: number) {
  if (currency === "INR") return `₹${(amount / 100).toFixed(0)}`;
  return `$${(amount / 100).toFixed(2)}`;
}

export function TenantInstructorStudentDetailPage() {
  const {
    tenantSlug = "",
    courseId = "",
    userId = "",
  } = useParams<{
    tenantSlug: string;
    courseId: string;
    userId: string;
  }>();
  const t = useTranslations("instructor");
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const detailQuery = useQuery({
    queryKey: ["staff-student-detail", courseId, userId],
    queryFn: () => fetchStaffStudentDetail(courseId, userId),
    enabled: isAuthenticated && isStaffRole(user?.role) && Boolean(courseId && userId),
  });

  if (!isAuthenticated || !isStaffRole(user?.role)) {
    return <Navigate to={`/t/${tenantSlug}/login`} replace />;
  }

  if (detailQuery.isLoading) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10" data-testid="staff-student-detail-loading">
        <p className="text-sm text-[var(--text-muted)]">{t("loading")}</p>
      </main>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10" data-testid="staff-student-detail-not-found">
        <p className="text-sm text-[var(--text-muted)]">{t("studentNotFound")}</p>
      </main>
    );
  }

  const { enrollment, metrics, submissions } = detailQuery.data;
  const paymentLabel =
    enrollment.paymentAmount != null && enrollment.paymentCurrency
      ? formatPrice(enrollment.paymentCurrency, enrollment.paymentAmount)
      : null;

  return (
    <main className="mx-auto max-w-3xl px-4 py-10" data-testid="staff-student-detail">
      <Link
        to={`/t/${tenantSlug}/instructor`}
        className="text-sm text-[var(--text-heading)] underline"
        data-testid="staff-student-back"
      >
        {t("backToTeaching")}
      </Link>
      <h1 className="mt-4 text-2xl font-semibold">
        {enrollment.studentName ?? enrollment.studentEmail}
      </h1>

      <section
        className="mt-6 rounded-lg border border-[var(--border)] p-4"
        data-testid="staff-student-profile"
      >
        <h2 className="font-medium">{t("studentProfileTitle")}</h2>
        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentNameLabel")}</dt>
            <dd data-testid="staff-student-name">{enrollment.studentName ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentEmailLabel")}</dt>
            <dd data-testid="staff-student-email">{enrollment.studentEmail}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentPhoneLabel")}</dt>
            <dd data-testid="staff-student-phone">{enrollment.studentPhone ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentStatusLabel")}</dt>
            <dd data-testid="staff-student-status">{enrollment.status}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentCohortLabel")}</dt>
            <dd data-testid="staff-student-cohort">{enrollment.cohortName}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentEnrolledAtLabel")}</dt>
            <dd data-testid="staff-student-enrolled-at">{formatWhen(enrollment.enrolledAt)}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentLastActiveLabel")}</dt>
            <dd data-testid="staff-student-last-active">{formatWhen(enrollment.lastActiveAt)}</dd>
          </div>
          <div>
            <dt className="text-[var(--text-muted)]">{t("studentPaymentLabel")}</dt>
            <dd data-testid="staff-student-payment">
              {paymentLabel
                ? `${paymentLabel}${enrollment.paymentStatus ? ` · ${enrollment.paymentStatus}` : ""}${
                    enrollment.paymentProvider ? ` · ${enrollment.paymentProvider}` : ""
                  }`
                : t("studentPaymentNone")}
            </dd>
          </div>
        </dl>
      </section>

      <section className="mt-8 rounded-lg border border-[var(--border)] p-4">
        <h2 className="font-medium">{t("studentProgressTitle")}</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2" data-testid="staff-student-metrics">
          <li>
            {t("progressLabel")}: {metrics.progressPercent}% ({metrics.completedLessons}/
            {metrics.totalLessons})
          </li>
          <li>
            {t("quizAvgLabel")}:{" "}
            {metrics.quizAvgPct == null ? "—" : `${Math.round(metrics.quizAvgPct)}%`}
          </li>
          <li>
            {t("hwSubmittedLabel")}: {metrics.homeworkSubmitted}/{metrics.homeworkTotal}
          </li>
          <li>
            {t("hwTimingLabel")}: on-time {metrics.homeworkOnTime} · late {metrics.homeworkLate} ·
            missing {metrics.homeworkMissing} · pending {metrics.homeworkPending}
          </li>
        </ul>
      </section>

      {/* LRN-7 (L3): quiz attempts with an instructor reset. */}
      <StaffStudentQuizAttempts courseId={courseId} userId={userId} />

      <section className="mt-8">
        <h2 className="font-medium">{t("studentHomeworkTitle")}</h2>
        {submissions.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--text-muted)]" data-testid="staff-student-hw-empty">
            {t("noStudentSubmissions")}
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="staff-student-hw-list">
            {submissions.map((row) => (
              <li
                key={row.submissionId}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-[var(--border)] px-3 py-2 text-sm"
              >
                <span>
                  {row.lessonTitle} · {row.fileName} · {formatWhen(row.submittedAt)}
                  {row.grade ? ` · ${t("gradeLabel")}: ${row.grade}` : ""}
                  {row.feedback ? ` · ${row.feedback}` : ""}
                </span>
                <ApiDownloadButton
                  path={`/v1/instructor/submissions/${row.submissionId}/download`}
                  fileName={row.fileName}
                  className="text-[var(--text-heading)] underline"
                  data-testid={`staff-student-hw-download-${row.submissionId}`}
                >
                  {t("download")}
                </ApiDownloadButton>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
